import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Text } from '../../../components/ui';
import { Theme } from '../../../utils/theme';

interface ProfileUpdate {
  coreProfile?: {
    goals?: string[];
    challenges?: string[];
    interests?: string[];
    values?: string[];
    personalityTraits?: string[];
  };
  activeContext?: {
    currentFocus?: string[];
    urgentNeeds?: string[];
    emotionalState?: string;
    information?: string;
  };
  changesSummary?: string;
}

interface ProfileUpdateCardProps {
  updates: ProfileUpdate;
}

interface DisplayItem {
  category: string;
  values: string[];
}

const ProfileUpdateCard: React.FC<ProfileUpdateCardProps> = ({ updates }) => {
  const items: DisplayItem[] = [];

  // Collect all updated fields (values are prefixed with + or -)
  if (updates.coreProfile) {
    if (updates.coreProfile.goals && updates.coreProfile.goals.length > 0) {
      items.push({ category: 'Goals', values: updates.coreProfile.goals });
    }
    if (updates.coreProfile.challenges && updates.coreProfile.challenges.length > 0) {
      items.push({ category: 'Challenges', values: updates.coreProfile.challenges });
    }
    if (updates.coreProfile.interests && updates.coreProfile.interests.length > 0) {
      items.push({ category: 'Interests', values: updates.coreProfile.interests });
    }
    if (updates.coreProfile.values && updates.coreProfile.values.length > 0) {
      items.push({ category: 'Values', values: updates.coreProfile.values });
    }
    if (updates.coreProfile.personalityTraits && updates.coreProfile.personalityTraits.length > 0) {
      items.push({ category: 'Personality Traits', values: updates.coreProfile.personalityTraits });
    }
  }

  if (updates.activeContext) {
    if (updates.activeContext.currentFocus && updates.activeContext.currentFocus.length > 0) {
      items.push({ category: 'Current Focus', values: updates.activeContext.currentFocus });
    }
    if (updates.activeContext.urgentNeeds && updates.activeContext.urgentNeeds.length > 0) {
      items.push({ category: 'Urgent Needs', values: updates.activeContext.urgentNeeds });
    }
    if (updates.activeContext.emotionalState) {
      items.push({ category: 'Emotional State', values: [updates.activeContext.emotionalState] });
    }
  }

  // If no updates, don't render anything
  if (items.length === 0) {
    return null;
  }

  const getCategoryIcon = (category: string): keyof typeof Ionicons.glyphMap => {
    switch (category) {
      case 'Goals':
        return 'flag';
      case 'Challenges':
        return 'alert-circle';
      case 'Interests':
        return 'heart';
      case 'Values':
        return 'star';
      case 'Personality Traits':
        return 'person';
      case 'Current Focus':
        return 'bulb';
      case 'Urgent Needs':
        return 'warning';
      case 'Emotional State':
        return 'happy';
      default:
        return 'information-circle';
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Ionicons name="checkmark-circle" size={20} color={Theme.colors.success} />
        <Text variant="body2" style={styles.headerText}>
          Profile Updated
        </Text>
      </View>

      {updates.changesSummary && (
        <Text variant="body2" style={styles.summaryText}>
          {updates.changesSummary}
        </Text>
      )}

      <View style={styles.changesContainer}>
        {items.map((item, itemIndex) => (
          <View key={`${item.category}-${itemIndex}`} style={styles.categorySection}>
            <View style={styles.categoryRow}>
              <Ionicons
                name={getCategoryIcon(item.category)}
                size={16}
                color={Theme.colors.success}
                style={styles.categoryIcon}
              />
              <Text variant="body2" style={styles.categoryLabel}>
                {item.category}
              </Text>
            </View>
            <View style={styles.valuesContainer}>
              {item.values.map((value, valueIndex) => {
                const isAdded = value.startsWith('+ ');
                const isRemoved = value.startsWith('- ');
                const displayValue = isAdded || isRemoved ? value.substring(2) : value;
                
                return (
                  <View 
                    key={`${item.category}-${value}-${valueIndex}`} 
                    style={[
                      styles.valueChip,
                      isRemoved && styles.valueChipRemoved
                    ]}
                  >
                    {(isAdded || isRemoved) && (
                      <Ionicons
                        name={isAdded ? 'add-circle' : 'remove-circle'}
                        size={14}
                        color={isAdded ? '#166534' : '#DC2626'}
                        style={styles.changeIcon}
                      />
                    )}
                    <Text 
                      variant="body2" 
                      style={[
                        styles.valueText,
                        isRemoved && styles.valueTextRemoved
                      ]}
                    >
                      {displayValue}
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#F0FDF4', // Light green background
    borderRadius: Theme.borderRadius.lg,
    borderLeftWidth: 4,
    borderLeftColor: Theme.colors.success,
    padding: Theme.spacing.md,
    marginTop: Theme.spacing.sm,
    gap: Theme.spacing.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.xs,
  },
  headerText: {
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.success,
  },
  summaryText: {
    color: '#166534', // Dark green
    lineHeight: 20,
    fontStyle: 'italic',
  },
  changesContainer: {
    gap: Theme.spacing.md,
  },
  categorySection: {
    gap: Theme.spacing.xs,
  },
  categoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.xs,
  },
  categoryIcon: {
    marginTop: 1,
  },
  categoryLabel: {
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.success,
  },
  valuesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Theme.spacing.xs,
    marginLeft: 24, // Align with category text
    maxWidth: '100%',
  },
  valueChip: {
    backgroundColor: '#DCFCE7', // Lighter green
    paddingHorizontal: Theme.spacing.sm,
    paddingVertical: 4,
    borderRadius: Theme.borderRadius.sm,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start', // Allow chip to size to its content
  },
  valueChipRemoved: {
    backgroundColor: '#FEE2E2', // Light red for removed items
  },
  changeIcon: {
    marginTop: 1,
  },
  valueText: {
    color: '#166534', // Dark green text
    fontSize: 13,
    flexShrink: 1, // Allow text to shrink but maintain content width
  },
  valueTextRemoved: {
    color: '#DC2626', // Red text for removed items
    textDecorationLine: 'line-through',
  },
});

export default ProfileUpdateCard;
